import { useMemo } from 'react';
import { twJoin } from 'tailwind-merge';

import { AccessLevelIcon, NodeStatus, ResourceTypeExplorer } from '@dbt-labs/dbt-dag';
import { ColumnDef, Icon, Link, RyeconLineage, Tooltip } from '@dbt-labs/sourdough';

import { AccessLevel, IssueType } from '@/__generated__/graphql';
import { NodeStatusIconBadge } from '@/components/NodeStatusIconBadge';
import { ResourceChip } from '@/components/ResourceChip';
import { LatestIssuesResponse } from '@/hooks/discovery/latestIssues';
import { useLinkToDbtCloud, useLinkToExplorer } from '@/hooks/links';
import { useUrlProjectParams } from '@/hooks/params';
import { formatAbsoluteLocalDate } from '@/util/date';

function getName(resource: LatestIssuesResponse['resource']) {
  if (!resource) return null;
  if ('name' in resource) return resource.name;
  return null;
}

function getAccess(resource: LatestIssuesResponse['resource']) {
  if (!resource) return null;
  if ('access' in resource) return resource.access;
  return null;
}

function getNameCellData(data: LatestIssuesResponse) {
  return {
    name: getName(data.resource) ?? data.uniqueId,
    uniqueId: data.uniqueId,
    access: getAccess(data.resource),
  };
}

const IssueTypeNames: { [key in IssueType]: string } = {
  ModelRun: 'Run',
  ModelTest: 'Test',
  SourceFreshness: 'Freshness',
};

type NameCellData = ReturnType<typeof getNameCellData>;

// type LatestUpdatesParams = {
//   now?: Date;
// };

// export const useLatestIssuesColumns = ({ now }: LatestUpdatesParams) => {
export const useLatestIssuesColumns = () => {
  const getLineageLink = useLinkToExplorer('projectLineage');
  const projectScope = useUrlProjectParams();
  const getRunLink = useLinkToDbtCloud('runDetails');
  const { accountId, projectId, environmentType } = useUrlProjectParams();
  const getDetailsPageLink = useLinkToExplorer('resourceDetails');
  // const currentTime = useCurrentTime(now ? () => now : undefined);

  const columns = useMemo((): ColumnDef<LatestIssuesResponse>[] => {
    return [
      {
        header: 'Name',
        accessorFn: getNameCellData,
        cell: ({ getValue }) => {
          const { name, uniqueId, access } = getValue() as NameCellData;
          return (
            <>
              <span className="flex w-full items-center">
                {
                  <AccessLevelIcon
                    className={twJoin('mr-2 mt-0.5 h-4')}
                    access={access as AccessLevel}
                    tooltipConfig={{ placement: 'right' }}
                  />
                }
                {
                  <Tooltip content={name} displayOnlyWhenTruncated className="w-full">
                    {(ref) => (
                      <div ref={ref} className="w-full truncate">
                        <Link
                          isInternal
                          to={getDetailsPageLink({
                            accountId,
                            environmentType,
                            projectId,
                            dbtUniqueId: uniqueId,
                          })}
                        >
                          {name}
                        </Link>
                      </div>
                    )}
                  </Tooltip>
                }
              </span>
            </>
          );
        },
      },
      {
        accessorKey: 'resource.resourceType',
        header: 'Resource',
        minSize: 155,
        size: 10,
        cell: ({ getValue }) => (
          <span className="space-x-1 whitespace-nowrap">
            <ResourceChip resourceType={getValue() as ResourceTypeExplorer} />
          </span>
        ),
      },
      {
        accessorKey: 'issueType',
        header: 'Issue type',
        accessorFn: ({ issueType }: { issueType: IssueType }) => {
          return IssueTypeNames[issueType];
        },
      },
      {
        accessorKey: 'status',
        header: 'Status',
        cell: ({ getValue }) => (
          <span className="space-x-1 whitespace-nowrap">
            <NodeStatusIconBadge status={getValue() as NodeStatus} size="small" />
          </span>
        ),
      },
      {
        accessorKey: 'runId',
        accessorFn: (data) => {
          switch (data?.resource?.__typename) {
            case 'SourceAppliedStateNestedNode':
              return {
                runId: data.runId || null,
                runDate: data.resource.sourceExec?.runDate || null,
              };
            case 'TestAppliedStateNestedNode':
              return {
                runId: data.runId || null,
                runDate: data.resource.testExec?.runDate || null,
              };
            case 'ModelAppliedStateNestedNode':
              return {
                runId: data.runId || null,
                runDate: data.resource.modelExec?.runDate || null,
              };
            default:
              return {};
          }
        },
        header: 'Latest run',
        cell: ({ getValue }) => {
          const { runId, runDate } = getValue() as any;
          if (!runId || !runDate) {
            return null;
          }
          return getRunLink ? (
            <Link isInternal={false} to={getRunLink({ ...projectScope, runId })}>
              {formatAbsoluteLocalDate(new Date(runDate))}
            </Link>
          ) : (
            formatAbsoluteLocalDate(new Date(runDate))
          );
        },
      },
      {
        accessorKey: 'uniqueId',
        accessorFn: (data) => ({
          uniqueId: data.uniqueId,
          filePath: data.resource?.filePath,
        }),
        header: 'Lineage',
        maxSize: 70,
        minSize: 70,
        size: 46,
        cell: (cellContext) => {
          const value = cellContext.getValue() as any;
          const filePath: string | null = value.filePath ?? null;
          const uniqueId: string = value.uniqueId;

          if (!filePath) return null;
          return (
            <Link
              className="mx-auto"
              isInternal
              to={getLineageLink({
                dbtUniqueId: uniqueId,
                accountId,
                environmentType,
                projectId,
                // NOTE(https://dbtlabs.atlassian.net/browse/META-3360): FQN is not yet exposed on the ...on Resource interface
                selector: `2+${filePath}+2`,
              })}
            >
              <Icon ryecon={RyeconLineage} />
            </Link>
          );
        },
      },
    ];
  }, [
    accountId,
    // currentTime,
    environmentType,
    getDetailsPageLink,
    getLineageLink,
    projectId,
    projectScope,
    getRunLink,
  ]);
  return columns;
};
